import { Children, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { debounce } from '@myrealtrip/modash';
import { BottomSheet, ButtonGeneral, Text } from '@myrealtrip/web-ui';

import { AGE_LIST, DEFAULT_TRAVELER_AGE as DEFAULT_FOCUS_AGE } from '~constants/pointToPointTicket';

import TravelerAgeSelectSheetOption from './TravelerAgeSelectSheetOption';

interface Props {
  isOpen: boolean;
  registeredTravelerCount: number;
  selectedTraveler: { index: number; age: number } | null;
  onApply: (age: number) => void;
  onModify: (index: number, age: number) => void;
  onClose: () => void;
}

const TravelerAgeSelectSheet = ({
  isOpen,
  registeredTravelerCount,
  selectedTraveler,
  onApply,
  onModify,
  onClose,
}: Props) => {
  const [age, setAge] = useState<number | null>(selectedTraveler?.age ?? DEFAULT_FOCUS_AGE);
  const [isShadowVisible, setShadowVisible] = useState(false);
  const travelerIndex = selectedTraveler ? selectedTraveler.index + 1 : registeredTravelerCount + 1;

  const scrollableRef = useRef<HTMLUListElement>(null);

  const closeSheet = () => {
    setShadowVisible(false);
    setAge(null);
    onClose();
  };

  const debouncedScroll = debounce(() => {
    if (scrollableRef.current) {
      setShadowVisible(scrollableRef.current.scrollTop > 5);
    }
  }, 5);

  const handleScroll = () => {
    debouncedScroll();
  };

  const handleClickAge = (age: number) => () => {
    setAge(age);
  };

  const handleApply = () => {
    if (age !== null) {
      if (selectedTraveler) {
        onModify(selectedTraveler.index, age);
      } else {
        onApply(age);
      }
      closeSheet();
    }
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      detent="content"
      headerProps={{
        titleAlign: 'left',
        style: {
          justifyContent: 'flex-start',
        },
        children: (
          <HeaderWrapper>
            <Text typography="paragraphBold20">나이 선택 - 탑승객 {travelerIndex}</Text>
            <Description>나이는 탑승일 기준으로 선택해주세요</Description>
          </HeaderWrapper>
        ),
        closable: true,
        hasGrapple: true,
      }}
      backdropProps={{
        onClick: closeSheet,
      }}
      preventScrollProps={{ ignoreIosScroll: true }}
      onClose={onClose}
      onXClick={onClose}
    >
      {isShadowVisible && <Shadow />}
      <ScrollContainer>
        <ScrollableAgeList ref={scrollableRef} onScroll={handleScroll}>
          {Children.toArray(
            AGE_LIST.map((displayAge, index) => (
              <TravelerAgeSelectSheetOption
                selectedAge={age}
                displayAge={displayAge}
                isOpen={isOpen}
                isLast={index === AGE_LIST.length - 1}
                onClick={handleClickAge}
              />
            )),
          )}
        </ScrollableAgeList>
      </ScrollContainer>
      <CTAContainer>
        <ButtonGeneral
          size="medium"
          shape="rectangle"
          disabled={age === null}
          onClick={handleApply}
        >
          <Text typography="headlineNormal15">적용하기</Text>
        </ButtonGeneral>
      </CTAContainer>
    </BottomSheet>
  );
};

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  padding-top: 18px;
`;

const Description = styled(Text)`
  color: ${({ theme }) => theme.colors.gray.s700};
`;

const Shadow = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 12px;
  z-index: 1;
  box-shadow: ${({ theme }) => theme.shadows.E100};
`;

const ScrollContainer = styled.div`
  padding: 15.5px 20px 89px;
`;

const ScrollableAgeList = styled.ul`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 195px;
  gap: 13px;

  overflow-y: auto;

  > li:first-of-type {
    margin-top: 8px;
  }

  > li:last-of-type {
    margin-bottom: 8px;
  }
`;

const CTAContainer = styled.footer`
  position: absolute;
  bottom: 0;
  width: 100%;
  display: flex;
  z-index: 2;
  justify-content: flex-end;
  align-items: center;
  background-color: ${({ theme: { palettes } }) => palettes.white};
  padding: 8px 24px calc(constant(safe-area-inset-bottom) + 8px);
  padding: 8px 24px calc(env(safe-area-inset-bottom) + 8px);
  box-shadow: ${({ theme: { shadows } }) => shadows.E100V};
`;

export default TravelerAgeSelectSheet;
